import clsx, { type ClassValue } from "clsx";

export function cn(...inputs: ClassValue[]): string {
  return clsx(inputs);
}

/**
 * Mask a contact name for privacy — first name visible, last name(s) replaced with *.
 * "Adarsh Badjate" → "Adarsh ******"
 * "John" → "John"
 * null/empty → "Unknown"
 */
export function maskContactName(name: string | null | undefined): string {
  if (!name || !name.trim()) return "Unknown";
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0];
  return [parts[0], ...parts.slice(1).map((p) => "*".repeat(p.length))].join(" ");
}

export function initials(name: string): string {
  const cleaned = name.replace(/\(.*?\)/g, "").trim();
  const first = cleaned.split(/[\s/]/)[0] ?? "";
  return (first.charAt(0) || "?").toUpperCase();
}

export function primaryOwner(owner: string): string {
  return owner.split(" / ")[0].split(",")[0].trim();
}

export function deterministicHue(seed: string): number {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

const MONTHS: Record<string, number> = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

/** Parse the free-form date strings from the Sheet (e.g. "May 4", "Apr 14–27"). Returns the first concrete date or null. */
export function parseLaunchDate(raw: string, year = 2026): Date | null {
  if (!raw) return null;
  const stripped = raw.replace(/^→\s*/, "").trim();
  const match = stripped.match(/([A-Za-z]{3,})\s*(\d{1,2})/);
  if (!match) return null;
  const monthKey = match[1].slice(0, 3).toLowerCase();
  const month = MONTHS[monthKey];
  if (month === undefined) return null;
  const day = parseInt(match[2], 10);
  return new Date(year, month, day);
}

export function daysUntil(date: Date, now: Date = new Date()): number {
  const msPerDay = 24 * 60 * 60 * 1000;
  const d1 = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const d2 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((d1.getTime() - d2.getTime()) / msPerDay);
}

/**
 * Parse start + end dates out of a free-form phase date string.
 * "Apr 15 – May 11" → { start: Apr 15, end: May 11 }
 * "Wk of Mar 23"    → { start: Mar 23, end: Mar 23 }
 * "Jun 22"          → { start: Jun 22, end: Jun 22 }
 */
export function parsePhaseDateRange(raw: string, year = 2026): { start: Date | null; end: Date | null } {
  if (!raw) return { start: null, end: null };
  const matches = [...raw.matchAll(/([A-Za-z]{3,})\s*(\d{1,2})/g)];
  if (matches.length === 0) return { start: null, end: null };
  const toDate = (m: RegExpMatchArray) => {
    const monthKey = m[1].slice(0, 3).toLowerCase();
    const month = MONTHS[monthKey];
    if (month === undefined) return null;
    return new Date(year, month, parseInt(m[2], 10));
  };
  const start = toDate(matches[0]);
  const end = matches.length > 1 ? toDate(matches[matches.length - 1]) : start;
  return { start, end };
}

import type { Phase } from "@/lib/types";

/**
 * Auto-derive phase statuses from today's date so the timeline never goes stale.
 * - Non-gate phase: active if today is within its date range; complete if past end; upcoming if before start.
 * - Gate: complete if today >= gate date AND the phase immediately before it is complete.
 * Sheet status is only used when no parseable date range exists.
 */
export function derivePhaseStatuses(phases: Phase[], now: Date = new Date()): Phase[] {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return phases.map((phase, idx) => {
    if (phase.isGate) {
      const { start } = parsePhaseDateRange(phase.date);
      const prevPhase = phases.slice(0, idx).reverse().find((p) => !p.isGate);
      const prevComplete = prevPhase?.status === "complete" || (
        start !== null && today >= start && phases.slice(0, idx).filter((p) => !p.isGate).every((p) => {
          const { end } = parsePhaseDateRange(p.date);
          return end !== null && today > end;
        })
      );
      if (start !== null && today >= start && prevComplete) return { ...phase, status: "complete" as const };
      return { ...phase, status: phase.status };
    }
    const { start, end } = parsePhaseDateRange(phase.date);
    if (!start) return phase;
    if (today > (end ?? start)) return { ...phase, status: "complete" as const };
    if (today >= start) return { ...phase, status: "active" as const };
    return { ...phase, status: "upcoming" as const };
  });
}

export function formatCountdown(days: number): string {
  if (days === 0) return "today";
  if (days === 1) return "1 day";
  if (days < 0) return `${Math.abs(days)}d late`;
  if (days < 7) return `${days} days`;
  if (days < 30) return `${Math.round(days / 7)}w`;
  return `${Math.round(days / 30)}mo`;
}
